import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import { MapPin, Minus, Phone, Plus, Send, X } from 'lucide-react';
import { Rating } from 'react-simple-star-rating';
import { useTranslations } from '../translations';
import {
  PRODUCT_LOCATION_MAP_MOBILE_ZOOM,
  MAP_TILE_ATTRIBUTION,
  PRODUCT_LOCATION_MAP_ZOOM,
  PRODUCT_LOCATION_RADIUS_METERS
} from '../constants/product';
import { MAX_PRODUCT_DETAIL_MESSAGE_LENGTH } from '../services/chat.api';
import { formatUnitType } from '../utils/unitType';
import ImageViewer from './ImageViewer';
import ProductImagePlaceholder from './ProductImagePlaceholder';
import './ProductPreviewModal.css';

export interface ProductPreviewData {
  productType: 'SELL' | 'BUY';
  title: string;
  description: string;
  price: {
    price: number;
    currency: string;
    unitType: string;
  };
  images: { preview: string }[];
  categoryName: string;
  subCategoryName?: string;
  locationName: string;
  countyName: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  negotiable: boolean;
  userFirstName: string;
  userLastName: string;
  userImageUrl: string | null;
  userPhone: string | null;
}

interface ProductPreviewModalProps {
  previewData: ProductPreviewData;
  onClose: () => void;
}

const ProductPreviewModal: React.FC<ProductPreviewModalProps> = ({
  previewData,
  onClose
}) => {
  const t = useTranslations();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [showPhoneNumber, setShowPhoneNumber] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  useEffect(() => {
    if (!previewData.coordinates) return;

    const initMap = () => {
      if (!mapContainerRef.current || !previewData.coordinates) return;

      if (mapRef.current) {
        mapRef.current.remove();
      }

      const { latitude, longitude } = previewData.coordinates;
      const mapZoom = window.matchMedia('(max-width: 768px)').matches
        ? PRODUCT_LOCATION_MAP_MOBILE_ZOOM
        : PRODUCT_LOCATION_MAP_ZOOM;

      const map = L.map(mapContainerRef.current, {
        center: [latitude, longitude],
        zoom: mapZoom,
        zoomSnap: 0.1,
        zoomControl: false
      });
      map.attributionControl.setPrefix(false);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: MAP_TILE_ATTRIBUTION
      }).addTo(map);

      const locationCircle = L.circle([latitude, longitude], {
        radius: PRODUCT_LOCATION_RADIUS_METERS,
        color: '#B12A00',
        weight: 2,
        opacity: 0.75,
        fillColor: '#B12A00',
        fillOpacity: 0.12,
        interactive: false
      }).addTo(map);

      map.fitBounds(locationCircle.getBounds(), {
        padding: [16, 16],
        maxZoom: mapZoom,
        animate: false
      });
      map.setZoom(mapZoom, { animate: false });

      mapRef.current = map;
    };

    const timer = setTimeout(initMap, 100);

    return () => {
      clearTimeout(timer);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [previewData.coordinates]);

  const handleZoomIn = () => {
    if (mapRef.current) {
      mapRef.current.zoomIn();
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      mapRef.current.zoomOut();
    }
  };

  const userPhoneNumber = previewData.userPhone?.trim() || '';
  const userPhoneDisplay = showPhoneNumber
    ? userPhoneNumber || t.productDetail.phoneUnavailable
    : '****';

  const handlePreviousImage = () => {
    if (previewData.images.length > 0 && currentImageIndex > 0) {
      setCurrentImageIndex((prev) => prev - 1);
    }
  };

  const handleNextImage = () => {
    if (previewData.images.length > 0 && currentImageIndex < previewData.images.length - 1) {
      setCurrentImageIndex((prev) => prev + 1);
    }
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentImageIndex < previewData.images.length - 1) {
      handleNextImage();
    }

    if (isRightSwipe && currentImageIndex > 0) {
      handlePreviousImage();
    }

    // Reset touch states
    setTouchStart(null);
    setTouchEnd(null);
  };

  return (
    <>
      {isViewerOpen && previewData.images.length > 0 && (
        <ImageViewer
          images={previewData.images.map(img => img.preview)}
          initialIndex={currentImageIndex}
          onClose={() => setIsViewerOpen(false)}
        />
      )}
      <div className="preview-modal-overlay" onClick={onClose}>
        <div className="preview-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="preview-modal-header">
          <h2 className="preview-modal-title">Previzualizare anunț</h2>
          <button type="button" className="preview-modal-close" onClick={onClose} aria-label={t.common.close}>
            <X size={22} aria-hidden="true" />
          </button>
        </div>

        <div className="preview-modal-content">
          <div className="preview-main-section">
            <div className="preview-left-section">
              <div className="preview-images-container">
                <div
                  className="preview-main-image-wrapper"
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                >
                  {previewData.images[currentImageIndex]?.preview ? (
                    <img
                      src={previewData.images[currentImageIndex].preview}
                      alt={previewData.title}
                      className="preview-main-image"
                      onClick={() => setIsViewerOpen(true)}
                      style={{ cursor: 'pointer' }}
                    />
                  ) : (
                    <ProductImagePlaceholder
                      className="preview-image-placeholder"
                      label={t.productDetail.noImage}
                    />
                  )}

                  {previewData.images.length > 0 && (
                    <div className="preview-image-counter">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M2 0H10C11.1027 0 12 0.897333 12 2V10C12 11.1027 11.1027 12 10 12H2C0.897333 12 0 11.1027 0 10V2C0 0.897333 0.897333 0 2 0ZM2 1.33333H10C10.368 1.33333 10.6667 1.632 10.6667 2V7.576L8.53133 5.75467C7.87133 5.19333 6.83867 5.19333 6.18467 5.75067L1.33333 9.79867V2C1.33333 1.632 1.632 1.33333 2 1.33333ZM4.33333 3.66667C4.33333 4.21867 3.88533 4.66667 3.33333 4.66667C2.78133 4.66667 2.33333 4.21867 2.33333 3.66667C2.33333 3.11467 2.78133 2.66667 3.33333 2.66667C3.88533 2.66667 4.33333 3.11467 4.33333 3.66667ZM2.374 10.6667H10C10.368 10.6667 10.6667 10.368 10.6667 10V9.32933L7.666 6.76933C7.50533 6.63133 7.208 6.63067 7.044 6.77L2.374 10.6667Z" fill="white" transform="translate(2, 2)"/>
                      </svg>
                      <span>{currentImageIndex + 1}/{previewData.images.length}</span>
                    </div>
                  )}

                  {previewData.images.length > 0 && (
                    <>
                      <button
                        className="preview-image-nav-button preview-image-nav-left"
                        onClick={handlePreviousImage}
                        disabled={currentImageIndex === 0}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14.0005 4.99993C14.2265 4.99993 14.4535 5.07593 14.6405 5.23193C15.0645 5.58493 15.1215 6.21593 14.7685 6.63993L10.2925 12.0109L14.6075 17.3729C14.9535 17.8029 14.8855 18.4329 14.4555 18.7789C14.0245 19.1249 13.3955 19.0569 13.0485 18.6269L8.22051 12.6269C7.92251 12.2559 7.92651 11.7259 8.23151 11.3599L13.2315 5.35993C13.4295 5.12293 13.7135 4.99993 14.0005 4.99993Z" fill="#596255"/>
                        </svg>
                      </button>

                      <button
                        className="preview-image-nav-button preview-image-nav-right"
                        onClick={handleNextImage}
                        disabled={currentImageIndex === previewData.images.length - 1}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9.9995 19.0001C9.7735 19.0001 9.5465 18.9241 9.3595 18.7681C8.9355 18.4151 8.8785 17.7841 9.2315 17.3601L13.7075 11.9891L9.3925 6.62707C9.0465 6.19707 9.1145 5.56707 9.5445 5.22107C9.9755 4.87507 10.6045 4.94307 10.9515 5.37307L15.7795 11.3731C16.0775 11.7441 16.0735 12.2741 15.7685 12.6401L10.7685 18.6401C10.5705 18.8771 10.2865 19.0001 9.9995 19.0001Z" fill="#071105"/>
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="preview-info-section">
                <div className="preview-type-section">
                  <div className={`preview-type-label ${previewData.productType === 'BUY' ? 'preview-type-label-buy' : 'preview-type-label-sell'}`}>
                    <span className="preview-type-caption">{t.productDetail.listingType}</span>
                    <span className="preview-type-value">
                      {previewData.productType === 'BUY'
                        ? t.productDetail.wantToBuy
                        : t.productDetail.wantToSell}
                    </span>
                  </div>
                </div>

                <div className="preview-title-price-container">
                  <h1 className="preview-title">{previewData.title}</h1>
                  <div className="preview-price-container">
                    <div className="preview-price">
                      {previewData.price.price} {previewData.price.currency}/{formatUnitType(previewData.price.unitType)}
                    </div>
                    {previewData.negotiable && <div className="preview-negotiable-label">{t.productDetail.negotiable}</div>}
                  </div>
                </div>

                <div className="preview-divider"></div>

                <div className="preview-rating-section">
                  <div className="preview-current-rating">
                    <Rating
                      initialValue={0}
                      readonly={true}
                      iconsCount={1}
                      size={24}
                      fillColor="#267200"
                      emptyColor="#D5DED1"
                      allowFraction={true}
                    />
                    <span className="preview-rating-value">0.0</span>
                  </div>

                  <div className="preview-rating-actions">
                    <span className="preview-rating-label">{t.productDetail.leaveRating}</span>
                    <div className="preview-rating-stars">
                      <Rating
                        initialValue={0}
                        size={24}
                        fillColor="#267200"
                        emptyColor="#D5DED1"
                        allowFraction={true}
                        readonly={true}
                      />
                    </div>
                  </div>
                </div>

                <div className="preview-divider"></div>

                <section className="preview-description-section" aria-labelledby="preview-description-title">
                  <div className="preview-description-header">
                    <h2 id="preview-description-title">{t.productDetail.descriptionTitle}</h2>
                  </div>
                  <div className="preview-description">
                    {previewData.description}
                  </div>
                </section>

                <div className="preview-time-report-container">
                  <div className="preview-time-submitted">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" clipRule="evenodd" d="M0 6.66667C0 2.99067 2.99067 0 6.66667 0C10.3427 0 13.3333 2.99067 13.3333 6.66667C13.3333 10.3427 10.3427 13.3333 6.66667 13.3333C2.99067 13.3333 0 10.3427 0 6.66667ZM6.66667 12C3.726 12 1.33333 9.60733 1.33333 6.66667C1.33333 3.726 3.726 1.33333 6.66667 1.33333C9.60733 1.33333 12 3.726 12 6.66667C12 9.60733 9.60733 12 6.66667 12ZM9.33333 6H7.33333V4C7.33333 3.63133 7.03467 3.33333 6.66667 3.33333C6.29867 3.33333 6 3.63133 6 4V6.66667C6 7.03533 6.29867 7.33333 6.66667 7.33333H9.33333C9.702 7.33333 10 7.03533 10 6.66667C10 6.298 9.702 6 9.33333 6Z" fill="#596255" transform="translate(1, 1)"/>
                    </svg>
                    <span className="preview-time-label">Previzualizare</span>
                  </div>
                </div>

                <div className="preview-divider" aria-hidden="true"></div>
              </div>
            </div>

            <div className="preview-seller-card">
              <div className="preview-seller-header">
                <div className="preview-seller-avatar">
                  {previewData.userImageUrl ? (
                    <img src={previewData.userImageUrl} alt={`${previewData.userFirstName} ${previewData.userLastName}`} className="preview-avatar-image" />
                  ) : (
                    <div className="preview-avatar-placeholder">
                      {previewData.userFirstName.charAt(0)}{previewData.userLastName.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="preview-seller-name">
                  {previewData.userFirstName} {previewData.userLastName}
                </div>
              </div>

              <div className="preview-seller-phone">
                <Phone aria-hidden="true" />
                <span className="preview-masked-phone">
                  {userPhoneDisplay}
                </span>
                <button
                  type="button"
                  className="preview-show-phone-button"
                  onClick={() => setShowPhoneNumber((currentValue) => !currentValue)}
                >
                  {showPhoneNumber ? t.productDetail.hidePhone : t.productDetail.showPhone}
                </button>
              </div>

              <div className="preview-divider"></div>

              <div className="preview-seller-message">
                <label className="preview-message-label">{t.productDetail.sendMessage}</label>
                <div className="preview-message-input-wrapper">
                  <textarea
                    className="preview-message-input"
                    placeholder={t.productDetail.messagePlaceholder}
                    maxLength={MAX_PRODUCT_DETAIL_MESSAGE_LENGTH}
                    disabled
                  ></textarea>
                  <span className="preview-message-character-count">
                    0/{MAX_PRODUCT_DETAIL_MESSAGE_LENGTH}
                  </span>
                  <button
                    type="button"
                    className="preview-send-message-button"
                    disabled
                    aria-label={t.productDetail.sendMessage}
                  >
                    <Send size={18} aria-hidden="true" />
                  </button>
                </div>
              </div>

              <div className="preview-divider"></div>

              <div className="preview-seller-location">
                <div className="preview-location-header">
                  <span className="preview-location-label">{t.productDetail.location}</span>
                  <div className="preview-location-display">
                    <MapPin aria-hidden="true" />
                    <span className="preview-location-text">{previewData.locationName}, {previewData.countyName}</span>
                  </div>
                </div>
                {previewData.coordinates && (
                  <div className="preview-location-map" ref={mapContainerRef}>
                    <div className="preview-map-zoom-controls">
                      <button type="button" className="preview-zoom-button" onClick={handleZoomIn} aria-label="Mărește harta">
                        <Plus size={18} aria-hidden="true" />
                      </button>
                      <div className="preview-zoom-divider"></div>
                      <button type="button" className="preview-zoom-button" onClick={handleZoomOut} aria-label="Micșorează harta">
                        <Minus size={18} aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="preview-divider"></div>

              <div className="preview-seller-rating-section">
                <div className="preview-rating-header">
                  <span className="preview-rating-label-seller">{t.productDetail.rateSellerLabel}</span>
                </div>
                <div className="preview-seller-rating-controls">
                  <div className="preview-current-seller-rating">
                    <Rating
                      initialValue={0}
                      readonly={true}
                      iconsCount={1}
                      size={24}
                      fillColor="#267200"
                      emptyColor="#D5DED1"
                      allowFraction={true}
                    />
                    <span className="preview-seller-rating-value">0.00</span>
                  </div>
                  <div className="preview-seller-rating-stars">
                    <Rating
                      initialValue={0}
                      size={24}
                      fillColor="#267200"
                      emptyColor="#D5DED1"
                      allowFraction={true}
                      readonly={true}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default ProductPreviewModal;
